/**
 * O bloco de uma casa na tela de Residências, com a tabela de lançamentos dela.
 *
 * Papel: reproduz o `.frota-block` da origem — 36px de margem abaixo, o título
 * serifado à esquerda e o subtítulo mono à direita, na mesma linha — com a
 * `TabelaDeLancamentos` embaixo.
 *
 * Como usar: o bloco de "Ambas / não se aplica" vem sem `subtitulo`. As ações
 * passam direto para a `TabelaDeLancamentos`, com as mesmas regras de lá. O
 * cabeçalho nunca recebe `acoes`: com um botão nele, o `CabecalhoDeSecao`
 * empilharia o subtítulo sob o título, e a origem o mantém à direita.
 */
import { type LancamentoResidencia } from '@concierge/shared';

import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import { TabelaDeLancamentos } from './tabela-de-lancamentos';

interface BlocoDeLancamentosProps {
  /** O nome da casa, ou "Ambas / não se aplica". */
  titulo: string;
  /** A cidade e a UF da casa, já em caixa alta; ausente no bloco "Ambas". */
  subtitulo?: string;
  /** Os lançamentos do bloco, na ordem de criação. */
  lancamentos: LancamentoResidencia[];
  /** Abre o formulário de lançamento novo; ausente esconde o "+ Novo lançamento". */
  onNovo?: () => void;
  /** Chamado com o lançamento da linha; ausente esconde a coluna de ações. */
  onEditar?: (lancamento: LancamentoResidencia) => void;
  /** Chamado com o lançamento a remover; a confirmação é de quem monta. */
  onRemover?: (lancamento: LancamentoResidencia) => void;
  /** Verdadeiro enquanto uma remoção está em andamento. */
  removendo?: boolean;
}

/**
 * O bloco de uma casa, ou o de "Ambas / não se aplica".
 *
 * @param titulo - O título do bloco, em `h3`.
 * @param subtitulo - A linha mono à direita do título; ausente no bloco "Ambas".
 * @param lancamentos - Os lançamentos do bloco, na ordem de criação.
 * @param onNovo - Repassado à tabela; ausente esconde o "+ Novo lançamento".
 * @param onEditar - Repassado à tabela; ausente esconde as ações.
 * @param onRemover - Repassado à tabela; ausente esconde as ações.
 * @param removendo - Repassado à tabela; desabilita "remover".
 * @returns A `<section>` do bloco.
 */
export function BlocoDeLancamentos({
  titulo,
  subtitulo,
  lancamentos,
  onNovo,
  onEditar,
  onRemover,
  removendo,
}: BlocoDeLancamentosProps) {
  return (
    <section className="mb-9">
      <CabecalhoDeSecao titulo={titulo} subtitulo={subtitulo} />
      <TabelaDeLancamentos
        lancamentos={lancamentos}
        onNovo={onNovo}
        onEditar={onEditar}
        onRemover={onRemover}
        removendo={removendo}
      />
    </section>
  );
}
